import { z } from "zod";
export * from "./week.ts";
export * from "./parse.ts";
export * from "./clientAnswers.ts";

// ---------- Enums ----------
export const Role = z.enum(["ADMIN", "TRAINER", "CLIENT"]);
export type Role = z.infer<typeof Role>;

export const Weekday = z.enum(["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]);
export type Weekday = z.infer<typeof Weekday>;
export const WEEKDAYS: Weekday[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

/** "HH:MM" 24h local time */
export const TimeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Expected HH:MM");

export const Phone = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{6,14}$/, "Use international format, e.g. +447700900123");

// ---------- Planning rhythm presets ----------
/** One choice that sets all the timing knobs. "custom" leaves them as they are. */
export const PLANNING_PRESETS = {
  thu: { label: "Thursday evening", blurb: "Check-ins Thu 18:00 · replies by Sat 06:00 · schedule out by Sun 18:00", availabilityRequestDay: "THU", availabilityRequestTime: "18:00", responseWindowHours: 36, publishDeadlineDay: "SUN", publishDeadlineTime: "18:00" },
  fri: { label: "Friday evening", blurb: "Check-ins Fri 18:00 · replies by Sun 06:00 · schedule out by Sun 18:00", availabilityRequestDay: "FRI", availabilityRequestTime: "18:00", responseWindowHours: 36, publishDeadlineDay: "SUN", publishDeadlineTime: "18:00" },
  sat: { label: "Saturday morning", blurb: "Check-ins Sat 09:00 · replies by Sun 09:00 · schedule out by Sun 18:00", availabilityRequestDay: "SAT", availabilityRequestTime: "09:00", responseWindowHours: 24, publishDeadlineDay: "SUN", publishDeadlineTime: "18:00" },
} as const satisfies Record<string, { label: string; blurb: string; availabilityRequestDay: Weekday; availabilityRequestTime: string; responseWindowHours: number; publishDeadlineDay: Weekday; publishDeadlineTime: string }>;
export type PlanningPreset = keyof typeof PLANNING_PRESETS;

/** Which preset (if any) a set of timings matches. */
export function matchPreset(t: { availabilityRequestDay: string; availabilityRequestTime: string; responseWindowHours: number; publishDeadlineDay: string; publishDeadlineTime: string }): PlanningPreset | "custom" {
  for (const [k, p] of Object.entries(PLANNING_PRESETS)) {
    if (p.availabilityRequestDay === t.availabilityRequestDay && p.availabilityRequestTime === t.availabilityRequestTime && p.responseWindowHours === t.responseWindowHours && p.publishDeadlineDay === t.publishDeadlineDay && p.publishDeadlineTime === t.publishDeadlineTime) return k as PlanningPreset;
  }
  return "custom";
}

/** A person's name: letters (any language), spaces, apostrophes, hyphens and dots. Keeps markup and junk out of lists and messages. */
export const PersonName = z
  .string()
  .trim()
  .min(2, "Please enter a name")
  .max(80)
  .regex(/^[\p{L}\p{M}][\p{L}\p{M}'’ .-]*$/u, "Please use just a name — letters, spaces, hyphens and apostrophes");

/** UK-friendly: "07700 900123" → "+447700900123"; "0044…" → "+44…". */
export function normalisePhone(raw: string): string | null {
  let p = raw.replace(/[\s\-().]/g, "");
  if (p.startsWith("00")) p = `+${p.slice(2)}`;
  else if (/^0\d{10}$/.test(p)) p = `+44${p.slice(1)}`;
  return /^\+[1-9]\d{6,14}$/.test(p) ? p : null;
}

// ---------- Auth ----------
export const RegisterTrainerInput = z.object({
  acceptTerms: z.boolean().refine((v) => v, "You need to accept the terms and privacy notice"),
  name: PersonName,
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(10).max(200),
  phone: Phone,
  /** Either an existing gym… */
  primaryGymId: z.string().uuid().optional(),
  /** …or a gym lookup result ("azmaps:…" / "gym:…")… */
  placeRef: z.string().min(4).max(200).optional(),
  /** How the gym was found (analytics) */
  gymResolvedVia: z.enum(["api", "disambiguated", "manual", "list"]).optional(),
  /** …or a new one, created (unverified) at sign-up */
  newGym: z.object({ name: z.string().trim().min(2).max(120), city: z.string().trim().max(80).optional(), postcode: z.string().trim().max(20).optional() }).optional(),
  sessionLengthMinutes: z.number().int().min(15).max(240).default(60),
  availabilityRequestDay: Weekday.default("THU"),
  availabilityRequestTime: TimeOfDay.default("18:00"),
  responseWindowHours: z.number().int().min(6).max(120).default(36),
  publishDeadlineDay: Weekday.default("SUN"),
  publishDeadlineTime: TimeOfDay.default("18:00"),
  /** Seconds the sign-up form was open (analytics only; optional) */
  signupDurationS: z.number().int().min(0).max(86_400).optional(),
}); // gym is asked in the setup chat (#3); API callers may still pass one
export type RegisterTrainerInput = z.infer<typeof RegisterTrainerInput>;

export const ForgotPasswordInput = z.object({ email: z.string().trim().toLowerCase().email() });
export const ResetPasswordInput = z.object({ token: z.string().min(16), password: z.string().min(10).max(200) });
export const ChangePasswordInput = z.object({ currentPassword: z.string().min(1), newPassword: z.string().min(10).max(200) });

export const LoginInput = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof LoginInput>;

// ---------- Trainer ----------
export const UpdateTrainerInput = z.object({
  name: PersonName.optional(),
  phone: Phone.optional(),
  sessionLengthMinutes: z.number().int().min(15).max(240).optional(),
  clientMovesEnabled: z.boolean().optional(),
  /** How close to a session clients can still move it by WhatsApp */
  moveCutoffMinutes: z.number().int().min(0).max(2880).optional(),
  availabilityRequestDay: Weekday.optional(),
  availabilityRequestTime: TimeOfDay.optional(),
  timezone: z.string().min(1).max(64).optional(),
  responseWindowHours: z.number().int().min(6).max(120).optional(),
  autoPublish: z.boolean().optional(),
  publishDeadlineDay: Weekday.optional(),
  publishDeadlineTime: TimeOfDay.optional(),
});
export type UpdateTrainerInput = z.infer<typeof UpdateTrainerInput>;

export const SetTrainerGymsInput = z
  .object({
    primaryGymId: z.string().uuid().optional(),
    placeRef: z.string().min(4).max(200).optional(),
    newGym: z.object({ name: z.string().trim().min(2).max(120), postcode: z.string().trim().min(2).max(20) }).optional(),
    gymResolvedVia: z.enum(["api", "disambiguated", "manual", "list"]).optional(),
  })
  .refine((v) => !!v.primaryGymId || !!v.placeRef || !!v.newGym, { message: "Pick your gym" });
export type SetTrainerGymsInput = z.infer<typeof SetTrainerGymsInput>;

// ---------- Availability (trainer's weekly template) ----------
/** The day RackUp schedules in (and the grid shows). */
export const HOURS_OPEN = "05:00";
export const HOURS_CLOSE = "22:00";
export const AvailabilitySlotInput = z
  .object({
    weekday: Weekday,
    startTime: TimeOfDay,
    endTime: TimeOfDay,
    /** null => trainer's primary gym */
    gymId: z.string().uuid().nullable().default(null),
  })
  .refine((s) => s.startTime < s.endTime, { message: "endTime must be after startTime" })
  .refine((s) => s.startTime >= HOURS_OPEN && s.endTime <= HOURS_CLOSE, { message: `Hours must be between ${HOURS_OPEN} and ${HOURS_CLOSE}` });
export type AvailabilitySlotInput = z.infer<typeof AvailabilitySlotInput>;

export const ReplaceAvailabilityInput = z.object({
  slots: z.array(AvailabilitySlotInput).max(100),
});

// ---------- Gyms ----------
export const CreateGymInput = z.object({
  name: z.string().trim().min(2).max(120),
  addressLine1: z.string().trim().max(200).optional(),
  city: z.string().trim().max(80).optional(),
  postcode: z.string().trim().max(20).optional(),
  country: z.string().trim().length(2).default("GB"),
});
export type CreateGymInput = z.infer<typeof CreateGymInput>;

// ---------- Clients ----------
export const ClientPreferenceInput = z
  .object({
    weekday: Weekday,
    /** Omit both times for a "general" slot (any time that day) */
    startTime: TimeOfDay.optional(),
    endTime: TimeOfDay.optional(),
    /** Empty => any of the trainer's gyms */
    gymIds: z.array(z.string().uuid()).default([]),
  })
  .refine((p) => (p.startTime === undefined) === (p.endTime === undefined), {
    message: "Provide both startTime and endTime, or neither",
  })
  .refine((p) => !p.startTime || !p.endTime || p.startTime < p.endTime, {
    message: "endTime must be after startTime",
  });
export type ClientPreferenceInput = z.infer<typeof ClientPreferenceInput>;

export const CreateClientInput = z.object({
  name: PersonName,
  phone: Phone,
  email: z.string().trim().toLowerCase().email().optional(),
  sessionsPerWeek: z.number().int().min(1).max(14).default(2),
  preferences: z.array(ClientPreferenceInput).max(30).default([]),
  /** Trainer confirms the client has agreed to be added and messaged */
  consentAttested: z.boolean().optional(),
});
export type CreateClientInput = z.infer<typeof CreateClientInput>;

export const UpdateClientInput = CreateClientInput.partial();

/** Client self-registration via trainer's private invite link */
export const ClientSelfSignupInput = CreateClientInput.extend({
  inviteToken: z.string().min(16),
  /** Only used to check they're 18 or over; never stored. */
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  password: z.string().min(10).max(200).optional(),
  acceptPrivacy: z.boolean(),
  confirmAdult: z.boolean(),
  whatsappOptIn: z.boolean().default(true),
});
export type ClientSelfSignupInput = z.infer<typeof ClientSelfSignupInput>;

// ---------- API response shapes ----------
export type ApiError = { error: string; details?: unknown };

export interface MeResponse {
  id: string;
  email: string;
  name: string;
  role: Role;
  policyVersion: string;
  acceptedPolicyVersion: string | null;
  trainer?: {
    id: string;
    phone: string | null;
    sessionLengthMinutes: number;
    availabilityRequestDay: Weekday;
    availabilityRequestTime: string;
    timezone: string;
    responseWindowHours: number;
    autoPublish: boolean;
    publishDeadlineDay: Weekday;
    publishDeadlineTime: string;
    onboarded: boolean;
    inviteToken: string;
    clientMovesEnabled: boolean;
    moveCutoffMinutes: number;
    /** Profile picture shown to clients on the sign-up link; null when none. */
    photoUrl: string | null;
    gyms: { id: string; name: string; isPrimary: boolean }[];
    subscriptionStatus: string;
  };
}
